import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  MessageSquare,
  Download,
  Loader2,
  Search,
  X,
  ArrowUpDown,
  Calendar,
  FileJson,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { PageHeader } from '@/components/PageHeader';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ConversationLog, ChatbotUser } from '@/core/models';

export const Route = createFileRoute('/conversations')({
  head: () => ({
    meta: [{ title: 'Conversazioni · Replya Internal Backoffice' }],
  }),
  component: ConversationsPage,
});

type SortOption = 'lastMessageAt-desc' | 'lastMessageAt-asc' | 'messages-desc' | 'messages-asc';

function formatDateTime(iso?: string) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('it-IT');
}

function escapeCsvCell(value: string | number | undefined) {
  const str = value === undefined || value === null ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildConversationCsv(rows: ConversationLog[]) {
  const headers = [
    'conversationId',
    'firebaseId',
    'userEmail',
    'userDisplayName',
    'messageCount',
    'firstMessageAt',
    'lastMessageAt',
    'summary',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((c) =>
      headers
        .map((h) => escapeCsvCell(c[h as keyof ConversationLog] as string | number | undefined))
        .join(','),
    ),
  ];
  return lines.join('\n');
}

function triggerDownload(payload: string, filename: string, type: string) {
  const blob = new Blob([payload], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ConversationRow({
  conversation,
  user,
}: {
  conversation: ConversationLog;
  user?: ChatbotUser;
}) {
  const navigate = useNavigate({ from: '/conversations' });

  const handleClick = () => {
    navigate({
      to: '/conversation/$conversationId',
      params: { conversationId: conversation.conversationId },
    });
  };

  return (
    <TableRow className="cursor-pointer hover:bg-surface-2" onClick={handleClick}>
      <TableCell>
        {conversation.lastMessageAt ? formatDateTime(conversation.lastMessageAt) : '-'}
      </TableCell>
      <TableCell className="font-mono text-xs">{conversation.conversationId}</TableCell>
      <TableCell>
        {user ? (
          <div className="text-sm">
            {user.firstName} {user.lastName}
            <span className="block text-xs text-muted-foreground">{user.email}</span>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">
            {conversation.userEmail || conversation.userDisplayName || conversation.firebaseId || 'anonimo'}
          </span>
        )}
      </TableCell>
      <TableCell>{conversation.messageCount ?? 0}</TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl"
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          title="Apri conversazione"
        >
          <MessageSquare className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function ConversationsPage() {
  const { selectedTenant } = useAppState();
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState<string>('__all__');
  const [sortBy, setSortBy] = useState<SortOption>('lastMessageAt-desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const { data: users } = useQuery({
    queryKey: ['chatbot-users'],
    queryFn: () => apiService.listUsers(),
  });

  const tenantUsers = useMemo(() => {
    if (!users || !selectedTenant) return [];
    return users.filter(
      (u) =>
        u.tenantIds.includes(selectedTenant.tenantId) ||
        u.tenantIds.length === 0,
    );
  }, [users, selectedTenant]);

  const { data: conversations, isLoading } = useQuery({
    queryKey: [
      'tenant-conversations',
      selectedTenant?.tenantId,
      userFilter,
      startDate,
      endDate,
    ],
    queryFn: () =>
      apiService.getConversationLogs(selectedTenant!.tenantId, {
        limit: 500,
        firebaseId: userFilter === '__all__' ? undefined : userFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !!selectedTenant,
  });

  const userMap = useMemo(() => new Map((users ?? []).map((u) => [u.firebaseId, u])), [users]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const items = conversations?.items ?? [];
    if (!term) return items;
    return items.filter((c) => {
      const user = c.firebaseId ? userMap.get(c.firebaseId) : undefined;
      const haystack = [
        c.conversationId,
        c.summary,
        c.userEmail,
        c.userDisplayName,
        c.firebaseId,
        user?.firstName,
        user?.lastName,
        user?.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [conversations, search, userMap]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    switch (sortBy) {
      case 'lastMessageAt-desc':
        return items.sort(
          (a, b) =>
            new Date(b.lastMessageAt ?? 0).getTime() - new Date(a.lastMessageAt ?? 0).getTime(),
        );
      case 'lastMessageAt-asc':
        return items.sort(
          (a, b) =>
            new Date(a.lastMessageAt ?? 0).getTime() - new Date(b.lastMessageAt ?? 0).getTime(),
        );
      case 'messages-desc':
        return items.sort((a, b) => (b.messageCount ?? 0) - (a.messageCount ?? 0));
      case 'messages-asc':
        return items.sort((a, b) => (a.messageCount ?? 0) - (b.messageCount ?? 0));
      default:
        return items;
    }
  }, [filtered, sortBy]);

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  const handleExportList = (format: 'json' | 'csv') => {
    if (!sorted.length) return;
    const filename = `conversazioni-${selectedTenant.tenantId}.${format}`;
    if (format === 'json') {
      triggerDownload(JSON.stringify(sorted, null, 2), filename, 'application/json');
    } else {
      triggerDownload(buildConversationCsv(sorted), filename, 'text/csv;charset=utf-8');
    }
  };

  const handleExportAll = async (format: 'json' | 'csv') => {
    if (!selectedTenant) return;
    setIsExporting(true);
    try {
      const all: ConversationLog[] = [];
      const pageSize = 500;
      const maxPages = 100;
      let offset = 0;
      let total = Infinity;

      while (offset < total && offset / pageSize < maxPages) {
        const res = await apiService.getConversationLogs(selectedTenant.tenantId, {
          limit: pageSize,
          offset,
          firebaseId: userFilter === '__all__' ? undefined : userFilter,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        });
        all.push(...res.items);
        total = res.total;
        if (res.items.length < pageSize) break;
        offset += pageSize;
      }

      const filename = `conversazioni-complete-${selectedTenant.tenantId}.${format}`;
      if (format === 'json') {
        triggerDownload(JSON.stringify(all, null, 2), filename, 'application/json');
      } else {
        triggerDownload(buildConversationCsv(all), filename, 'text/csv;charset=utf-8');
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Conversazioni"
        subtitle={`Audit delle conversazioni del chatbot per ${selectedTenant.name}.`}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              disabled={isExporting || (!sorted.length && !conversations?.items.length)}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Esporta
              <ChevronDown className="ml-2 h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Elenco filtrato</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleExportList('json')}>
              <FileJson className="mr-2 h-4 w-4" />
              JSON
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportList('csv')}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              CSV
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Tutto il dataset</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleExportAll('json')}>
              <FileJson className="mr-2 h-4 w-4" />
              JSON completo
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportAll('csv')}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              CSV completo
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="flex flex-col gap-3 rounded-2xl border bg-surface p-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Cerca per ID, email, nome utente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-xl bg-surface-2 border-border pl-9"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="rounded-xl bg-surface-2 border-border md:w-64">
            <SelectValue placeholder="Tutti gli utenti" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti gli utenti</SelectItem>
            {tenantUsers.map((u) => (
              <SelectItem key={u.firebaseId} value={u.firebaseId}>
                {u.firstName} {u.lastName} ({u.email})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
          <SelectTrigger className="rounded-xl bg-surface-2 border-border md:w-56">
            <ArrowUpDown className="mr-2 h-4 w-4 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lastMessageAt-desc">Data ultimo messaggio ↓</SelectItem>
            <SelectItem value="lastMessageAt-asc">Data ultimo messaggio ↑</SelectItem>
            <SelectItem value="messages-desc">Numero messaggi ↓</SelectItem>
            <SelectItem value="messages-asc">Numero messaggi ↑</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-40">
            <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-xl bg-surface-2 border-border pl-9"
              placeholder="Dal"
            />
          </div>
          <span className="text-muted-foreground">-</span>
          <div className="relative flex-1 md:w-40">
            <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-xl bg-surface-2 border-border pl-9"
              placeholder="Al"
            />
          </div>
        </div>

        <Badge variant="secondary" className="text-xs shrink-0">
          {sorted.length} conversazioni
        </Badge>
      </div>

      <div className="rounded-2xl border bg-surface overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data ultimo messaggio</TableHead>
                <TableHead>ID conversazione</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Messaggi</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                    Caricamento...
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Nessuna conversazione trovata.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((conversation) => {
                  const user = conversation.firebaseId
                    ? userMap.get(conversation.firebaseId)
                    : undefined;
                  return (
                    <ConversationRow
                      key={conversation.conversationId}
                      conversation={conversation}
                      user={user}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
